// @flow
import * as React from 'react';

import { X } from 'lucide-react';

import { useDispatch } from 'react-redux';
import { TextField, View, Button, FormControl } from 'reshaped';

import { getCart, addDiscount } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

// Normalizes different onChange payloads (string, option object, or event)
// to always return a plain string value for the form field
function extractValue(v: any): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    if (typeof v.value === 'string') return v.value;
    if (v.target && typeof v.target.value === 'string') return v.target.value;
  }
  return '';
}

type RefType = HTMLInputElement;

export function DiscountInput({
  ref,
}: {
  ref: React.RefSetter<RefType | null>,
}): React.MixedElement | null {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  async function handleSubmit(): Promise<void> | void {
    if (!value) return;
    setLoading(true);
    try {
      await dispatch(addDiscount(value));
      dispatch(getCart());
      setValue('');
    } catch (err) {
      setError(
        __('The code %(code)s is not valid.', {
          code: value,
        }),
      );
    } finally {
      setLoading(false);
    }
  }

  function handleRemove() {
    setValue('');
    setError(null);
  }

  return (
    <FormControl hasError={!!error}>
      {/* $FlowFixMe[incompatible-type] */}
      <TextField
        id="discount-code"
        name="discount-code"
        value={value}
        placeholder={__('Enter promo code here')}
        onChange={evt => {
          setValue(extractValue(evt));
          setError(null);
        }}
        inputAttributes={{
          style: { height: 'inherit' },
          title: __('Enter promo code here'),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          },
          ref,
        }}
        size="large"
        endSlotPadding={1}
        endSlot={
          <View paddingBlock={1}>
            <Button
              color="primary"
              loading={loading}
              size="medium"
              onClick={handleSubmit}
            >
              {__('Submit')}
            </Button>
          </View>
        }
        startSlot={
          value && (
            <View paddingBlock={1}>
              <Button icon={X} variant="ghost" onClick={handleRemove} />
            </View>
          )
        }
      />
      <FormControl.Error key={error}>{error}</FormControl.Error>
    </FormControl>
  );
}
